/**
 * Writeback-tool authoring approval cards — create (`add_db_write_tool`)
 * and bind (`use_writeback_tool`). Shows the parameterized SQL definition
 * and reason so the operator is not looking at a Cypher card. Registered
 * as a side effect on import.
 */
import type { InFlightAgentActionProposal } from '@axilon-apps/domains-ontology-ui-state'
import { useGetCustomAgentConfigQuery } from '@axilon-apps/domains-ontology-ui-state'
import { displayDbConnectionName } from '../../../lib/display-db-connection-name'
import { registerAgentActionView, type AgentActionViewCapability } from './viewRegistry'

type CreateWritebackToolProposal = Extract<
  InFlightAgentActionProposal,
  { kind: 'create_writeback_tool' }
>
type BindWritebackToolProposal = Extract<
  InFlightAgentActionProposal,
  { kind: 'bind_writeback_tool' }
>

function isCreateWritebackTool(p: InFlightAgentActionProposal): p is CreateWritebackToolProposal {
  return p.kind === 'create_writeback_tool'
}

function isBindWritebackTool(p: InFlightAgentActionProposal): p is BindWritebackToolProposal {
  return p.kind === 'bind_writeback_tool'
}

function AgentConfigLabel({ configId, configName }: { configId: string; configName?: string }) {
  const { data } = useGetCustomAgentConfigQuery(configId, {
    skip: !configId || Boolean(configName?.trim())
  })
  const label = configName?.trim() || data?.name?.trim() || configId
  return (
    <span className="font-mono" title={configId}>
      {label}
    </span>
  )
}

function ParamList({ params }: { params: string[] }) {
  if (params.length === 0) {
    return <span className="text-muted-foreground">none</span>
  }
  return <span className="font-mono">{params.map((p) => `:${p}`).join(', ')}</span>
}

function CreateProposalDetail({ proposal }: { proposal: CreateWritebackToolProposal }) {
  return (
    <div className="rounded-xl border border-line/70 bg-muted/30 p-2 text-11 text-foreground">
      <div className="grid gap-1.5">
        <div className="break-words">
          <span className="text-muted-foreground">Tool:</span>{' '}
          <span className="font-mono">db_write_{proposal.name}</span>
          <span className="text-muted-foreground"> ({proposal.toolKind})</span>
        </div>
        {proposal.configId ? (
          <div className="break-words">
            <span className="text-muted-foreground">Bind to agent:</span>{' '}
            <AgentConfigLabel configId={proposal.configId} configName={proposal.configName} />
          </div>
        ) : null}
        <div className="break-words">
          <span className="text-muted-foreground">Database:</span>{' '}
          <span className="font-mono" title={proposal.connectionId || undefined}>
            {displayDbConnectionName(proposal.connectionId)}
          </span>
        </div>
        <div className="break-words">
          <span className="text-muted-foreground">Params:</span>{' '}
          <ParamList params={proposal.params} />
        </div>
        <pre className="m-0 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md bg-background/60 p-2 font-mono text-10 leading-relaxed">
          {proposal.parametrizedQuery}
        </pre>
        {proposal.reason ? (
          <div className="break-words">
            <span className="text-muted-foreground">Why:</span> {proposal.reason}
          </div>
        ) : null}
        <div className="break-words text-muted-foreground">
          Approving creates the org registry tool
          {proposal.configId ? ' and binds it to this agent' : ''}. The runner will call it as{' '}
          <span className="font-mono">db_write_{proposal.name}</span>, not free-form SQL.
        </div>
      </div>
    </div>
  )
}

function BindProposalDetail({ proposal }: { proposal: BindWritebackToolProposal }) {
  const toolLabel = proposal.name ? `db_write_${proposal.name}` : 'Writeback tool'
  return (
    <div className="rounded-xl border border-line/70 bg-muted/30 p-2 text-11 text-foreground">
      <div className="grid gap-1.5">
        <div className="break-words">
          <span className="text-muted-foreground">Tool:</span>{' '}
          <span className="font-mono" title={proposal.toolId || undefined}>
            {toolLabel}
          </span>
        </div>
        <div className="break-words">
          <span className="text-muted-foreground">Agent:</span>{' '}
          <AgentConfigLabel configId={proposal.configId} configName={proposal.configName} />
        </div>
        {proposal.reason ? (
          <div className="break-words">
            <span className="text-muted-foreground">Why:</span> {proposal.reason}
          </div>
        ) : null}
        <div className="break-words text-muted-foreground">
          Approving binds this existing writeback tool to the agent. The runner will get{' '}
          <span className="font-mono">
            {proposal.name ? `db_write_${proposal.name}` : 'db_write_<name>'}
          </span>{' '}
          from the frozen snapshot.
        </div>
      </div>
    </div>
  )
}

function CreateSettledRowDetail({ proposal }: { proposal: CreateWritebackToolProposal }) {
  const terminal = proposal.terminalState
  if (!terminal) return null
  if (terminal.kind === 'applied') {
    const name = proposal.createdTool?.name ?? proposal.name
    const agent = proposal.configName?.trim()
    return (
      <p className="px-1 text-10 text-emerald-600 dark:text-emerald-400">
        Created {name}
        {proposal.bound || proposal.configId ? ` and bound to ${agent || 'the agent'}` : ''}.
      </p>
    )
  }
  if (terminal.kind === 'apply_failed') {
    return <p className="px-1 text-10 text-destructive">Create failed — {terminal.error}</p>
  }
  return (
    <p className="px-1 text-10 text-muted-foreground">
      Rejected{proposal.rejectionReason ? ` — ${proposal.rejectionReason}` : ''}.
    </p>
  )
}

function BindSettledRowDetail({ proposal }: { proposal: BindWritebackToolProposal }) {
  const terminal = proposal.terminalState
  if (!terminal) return null
  if (terminal.kind === 'applied') {
    const name = proposal.boundTool?.name ?? proposal.name ?? proposal.toolId
    const agent = proposal.configName?.trim() || 'the agent'
    return (
      <p className="px-1 text-10 text-emerald-600 dark:text-emerald-400">
        Bound {name} to {agent}.
      </p>
    )
  }
  if (terminal.kind === 'apply_failed') {
    return <p className="px-1 text-10 text-destructive">Bind failed — {terminal.error}</p>
  }
  return (
    <p className="px-1 text-10 text-muted-foreground">
      Rejected{proposal.rejectionReason ? ` — ${proposal.rejectionReason}` : ''}.
    </p>
  )
}

export const createWritebackToolViewCapability: AgentActionViewCapability = {
  kind: 'create_writeback_tool',
  badgeLabel() {
    return 'db write'
  },
  renderDetail(proposal) {
    if (!isCreateWritebackTool(proposal)) return null
    return <CreateProposalDetail proposal={proposal} />
  },
  renderSettledSummary(proposal) {
    if (!isCreateWritebackTool(proposal)) return null
    return <CreateSettledRowDetail proposal={proposal} />
  }
}

export const bindWritebackToolViewCapability: AgentActionViewCapability = {
  kind: 'bind_writeback_tool',
  badgeLabel() {
    return 'bind write'
  },
  renderDetail(proposal) {
    if (!isBindWritebackTool(proposal)) return null
    return <BindProposalDetail proposal={proposal} />
  },
  renderSettledSummary(proposal) {
    if (!isBindWritebackTool(proposal)) return null
    return <BindSettledRowDetail proposal={proposal} />
  }
}

registerAgentActionView(createWritebackToolViewCapability)
registerAgentActionView(bindWritebackToolViewCapability)
